import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(SplitText, ScrollTrigger);

const DEFAULT_CONFIG = {
  durations: {
    splitText: 0.9,
    fadeUp: 0.8,
    fadeDown: 0.8,
    typewriter: 0.1,
    listItems: 0.6
  },
  easing: {
    default: 'power3.out'
  },
  stagger: {
    splitText: 0.08,
    listItems: 0.12
  }
};

const OBSERVER_OPTIONS = {
  threshold: 0.15,
  rootMargin: '0px 0px -10% 0px'
};

// Selettore comune per tutti gli effetti supportati
const EFFECT_SELECTOR = '.effect-split-text, .effect-fade-up, .effect-fade-down, .effect-typewriter, .effect-list-items, .home-title, .home-title-logo';

// Selettore per escludere elementi che contengono widget parallax
const PARALLAX_CONTAINER_SELECTOR = '[data-widget-id^="home-parallax-"], [data-parallax-speed]';

const TITLE_SPLIT_EXCLUDE_SELECTORS = [
  '.arch_cant-sub',
  '.italy-map-section',
  '[data-split-ignore]'
];

function createPlaceholderId() {
  return `split-ignore-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function findPlaceholder(root, placeholderId) {
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_COMMENT,
    {
      acceptNode(node) {
        return node.nodeValue === placeholderId
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      }
    }
  );
  return walker.nextNode();
}

function restoreExcludedNodes(root, nodes) {
  nodes.forEach(item => {
    const placeholder = findPlaceholder(root, item.placeholderId);
    if (!placeholder || !placeholder.parentNode) {
      return;
    }
    placeholder.parentNode.insertBefore(item.node, placeholder);
    placeholder.parentNode.removeChild(placeholder);
  });
}

// Store split text instances for cleanup
let splitTextInstances = [];
let titleScrollTriggers = [];
let currentObserver = null;
let homeTitleObserver = null;

// Track animated elements to prevent re-animation
const animatedElements = new Set();

// Helper function to check if element contains parallax widgets
function containsParallaxWidgets(element) {
  if (!element) return false;
  
  if (element.matches && element.matches(PARALLAX_CONTAINER_SELECTOR)) {
    return true;
  }
  
  if (element.querySelector && element.querySelector(PARALLAX_CONTAINER_SELECTOR)) {
    return true;
  }
  
  return false;
}

// Cleanup function to revert split text
function cleanupSplitText() {
  splitTextInstances.forEach(instance => {
    if (instance && instance.revert) {
      instance.revert();
    }
  });
  splitTextInstances = [];
}

// Cleanup function for ScrollTrigger instances
function cleanupScrollTriggers() {
  titleScrollTriggers.forEach(trigger => {
    trigger.kill();
  });
  titleScrollTriggers = [];
}

// Cleanup function to disconnect observers
function cleanupObserver() {
  if (currentObserver) {
    currentObserver.disconnect();
    currentObserver = null;
  }
  if (homeTitleObserver) {
    homeTitleObserver.disconnect();
    homeTitleObserver = null;
  }
}

// Reset animated elements tracking
function resetAnimatedElements() {
  animatedElements.clear();
}

// Export reset function for manual control
export function resetTextEffects() {
  resetAnimatedElements();
  cleanupScrollTriggers();
  cleanupObserver();
  cleanupSplitText();
  
  // Semplice reset: lascia che GSAP gestisca il resto
  gsap.set(EFFECT_SELECTOR, { clearProps: "all" });
}

// Initialize text effects with scroll-based animations
export function initTextEffects(initialDelay = 0) {
  // Cleanup any existing instances
  cleanupSplitText();
  cleanupScrollTriggers();
  cleanupObserver();
  resetAnimatedElements();

  // Trova elementi da animare
  const allEffects = Array.from(document.querySelectorAll(EFFECT_SELECTOR));
  const elementsToAnimate = allEffects.filter(el => {
    const parentHasEffect = el.parentElement && el.parentElement.closest(EFFECT_SELECTOR);
    
    if (containsParallaxWidgets(el)) {
      console.log('⚠️ initTextEffects: escluso elemento con widget parallax:', el);
      return false;
    }
    
    return !parentHasEffect;
  });
  
  if (elementsToAnimate.length === 0) {
    console.log('⚠️ initTextEffects: nessun elemento da animare trovato');
    return;
  }

  // Nascondi inizialmente tutti gli elementi
  gsap.set(elementsToAnimate, { 
    opacity: 0,
    visibility: 'hidden'
  });
  
  // Nascondi anche tutti gli elementi .home-title-logo
  document.querySelectorAll('.home-title-logo').forEach(el => {
    if (!containsParallaxWidgets(el)) {
      gsap.set(el, { 
        opacity: 0,
        visibility: 'hidden'
      });
    }
  });

  // Create Intersection Observer for scroll-based animations
  currentObserver = new IntersectionObserver((entries) => {
    requestAnimationFrame(() => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          
          if (animatedElements.has(el)) {
            return;
          }
          
          if (containsParallaxWidgets(el)) {
            currentObserver.unobserve(el);
            return;
          }
          
          animatedElements.add(el);
          
          const delay = parseFloat(el.dataset.animationDelay) || 0;
          
          if (el.classList.contains('effect-split-text')) {
            animateSplitText(el, delay);
          } else if (el.classList.contains('effect-fade-up')) {
            animateFadeUp(el, delay);
          } else if (el.classList.contains('effect-fade-down')) {
            animateFadeDown(el, delay);
          } else if (el.classList.contains('effect-typewriter')) {
            animateTypewriter(el, delay);
          } else if (el.classList.contains('effect-list-items')) {
            animateListItems(el, delay);
          } else if (el.classList.contains('home-title')) {
            animateTitleBox(el, delay);
          } else if (el.classList.contains('home-title-logo')) {
            animateTitleLogo(el, delay);
          }
          
          currentObserver.unobserve(el);
        }
      });
    });
  }, OBSERVER_OPTIONS);

  // Osserva gli elementi
  document.querySelectorAll('.effect-split-text').forEach(el => {
    if (!containsParallaxWidgets(el)) {
      currentObserver.observe(el);
    }
  });

  document.querySelectorAll('.effect-fade-up').forEach(el => {
    if (!el.classList.contains('agricole-cards-grid') && !containsParallaxWidgets(el)) {
      currentObserver.observe(el);
    }
  });

  document.querySelectorAll('.effect-fade-down').forEach(el => {
    if (!containsParallaxWidgets(el)) {
      currentObserver.observe(el);
    }
  });

  document.querySelectorAll('.effect-typewriter').forEach(el => {
    if (!containsParallaxWidgets(el)) {
      currentObserver.observe(el);
    }
  });

  document.querySelectorAll('.effect-list-items').forEach(el => {
    if (!containsParallaxWidgets(el)) {
      currentObserver.observe(el);
    }
  });

  // Observer separato per .home-title
  homeTitleObserver = new IntersectionObserver((entries) => {
    requestAnimationFrame(() => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          
          if (animatedElements.has(el)) {
            return;
          }
          
          if (containsParallaxWidgets(el)) {
            homeTitleObserver.unobserve(el);
            return;
          }
          
          animatedElements.add(el);
          const delay = parseFloat(el.dataset.animationDelay) || 0;
          
          if (el.classList.contains('home-title-logo')) {
            animateTitleLogo(el, delay);
          } else {
            animateTitleBox(el, delay);
          }
          
          homeTitleObserver.unobserve(el);
        }
      });
    });
  }, {
    threshold: 0.01,
    rootMargin: '50% 0% 50% 0%'
  });

  document.querySelectorAll('.home-title, .home-title-logo').forEach(el => {
    if (!containsParallaxWidgets(el)) {
      const isInTitleBox = el.closest('.title-box') || el.closest('.title-box-mob');
      
      if (isInTitleBox) {
        animatedElements.add(el);
        const delay = parseFloat(el.dataset.animationDelay) || 0;
        
        if (el.classList.contains('home-title-logo')) {
          animateTitleLogo(el, delay);
        } else {
          animateTitleBox(el, delay);
        }
      } else {
        homeTitleObserver.observe(el);
      }
    }
  });

  // Attivazione elementi nel viewport
  setTimeout(() => {
    document.querySelectorAll(EFFECT_SELECTOR).forEach(el => {
      if (animatedElements.has(el) || containsParallaxWidgets(el)) {
        return;
      }
      
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      if (el.classList.contains('home-title')) {
        const isInViewport = rect.top < viewportHeight && rect.bottom > 0;
        const isAboveViewport = rect.bottom < 0;
        const isInTitleBox = el.closest('.title-box') || el.closest('.title-box-mob');
        
        if (isInTitleBox || isInViewport || isAboveViewport) {
          animatedElements.add(el);
          const delay = parseFloat(el.dataset.animationDelay) || 0;
          
          if (el.classList.contains('home-title-logo')) {
            animateTitleLogo(el, delay);
          } else {
            animateTitleBox(el, delay);
          }
          
          currentObserver.unobserve(el);
        }
        return;
      }
      
      const earlyActivationZone = viewportHeight * 1.5;
      const isVisible = rect.top < earlyActivationZone && rect.bottom > -viewportHeight * 0.5;
      
      if (isVisible) {
        animatedElements.add(el);
        const delay = parseFloat(el.dataset.animationDelay) || 0;
        
        if (el.classList.contains('effect-split-text')) {
          animateSplitText(el, delay);
        } else if (el.classList.contains('effect-fade-up')) {
          animateFadeUp(el, delay);
        } else if (el.classList.contains('effect-fade-down')) {
          animateFadeDown(el, delay);
        } else if (el.classList.contains('effect-typewriter')) {
          animateTypewriter(el, delay);
        } else if (el.classList.contains('effect-list-items')) {
          animateListItems(el, delay);
        }
        
        currentObserver.unobserve(el);
      }
    });
    
    // Fallback per .home-title
    setTimeout(() => {
      document.querySelectorAll('.home-title, .home-title-logo').forEach(el => {
        if (!animatedElements.has(el) && !containsParallaxWidgets(el)) {
          animatedElements.add(el);
          const delay = parseFloat(el.dataset.animationDelay) || 0;
          
          if (el.classList.contains('home-title-logo')) {
            animateTitleLogo(el, delay);
          } else {
            animateTitleBox(el, delay);
          }
          
          if (currentObserver) {
            currentObserver.unobserve(el);
          }
        }
      });
    }, 100);
  }, 30);

  // Handle window resize - SEMPLIFICATO
  let lastInnerWidth = window.innerWidth;
  let resizeTimeout;
  
  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      // Solo se cambia la larghezza
      if (window.innerWidth === lastInnerWidth) {
        return;
      }
      
      lastInnerWidth = window.innerWidth;
      
      // Semplice cleanup e re-init
      cleanupSplitText();
      cleanupScrollTriggers();
      
      // Re-init delle animazioni
      initTextEffects(0);
      
      // Refresh ScrollTrigger
      if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
      }
    }, 150);
  };
  
  window.addEventListener('resize', handleResize, { passive: true });
}

// Animation functions

export function animateSplitText(el, delay) {
  if (!el || !el.textContent) {
    return;
  }
  
  const config = DEFAULT_CONFIG;
  
  gsap.set(el, { opacity: 0, visibility: 'visible' });
  
  try {
    const split = SplitText.create(el, {
      type: 'lines',
      linesClass: 'split-line',
      autoSplit: true
    });
    
    if (!split.lines || split.lines.length === 0) {
      gsap.set(el, { opacity: 1, visibility: 'visible' });
      return;
    }
    
    splitTextInstances.push(split);
    
    gsap.set(split.lines, { 
      opacity: 0, 
      yPercent: 100, 
      display: 'flex', 
      alignItems: 'center'
    });
    
    gsap.set(el, { visibility: 'visible', opacity: 1 });
    
    gsap.to(split.lines, {
      opacity: 1,
      yPercent: 0,
      duration: config.durations.splitText,
      delay: delay,
      ease: config.easing.default,
      stagger: config.stagger.splitText
    });
  } catch (error) {
    console.error('❌ Errore in animateSplitText:', error);
    gsap.set(el, { opacity: 1, visibility: 'visible' });
  }
}

function animateTitleBox(el, delay) {
  const config = DEFAULT_CONFIG;
  
  const excludedNodes = [];
  if (TITLE_SPLIT_EXCLUDE_SELECTORS.length > 0) {
    const selector = TITLE_SPLIT_EXCLUDE_SELECTORS.join(', ');
    el.querySelectorAll(selector).forEach(node => {
      if (!node || !node.parentNode) {
        return;
      }
      const placeholderId = createPlaceholderId();
      const placeholder = document.createComment(placeholderId);
      node.parentNode.insertBefore(placeholder, node);
      node.parentNode.removeChild(node);
      excludedNodes.push({ node, placeholderId });
    });
  }

  gsap.set(el, { opacity: 0, visibility: 'visible' });
  
  const triggerElement = el.closest('[data-title-anchor]') || el;
  const useWrapperTrigger = triggerElement !== el;
  
  const split = SplitText.create(el, {
    type: 'lines',
    linesClass: 'split-line',
    autoSplit: true
  });

  if (excludedNodes.length > 0) {
    restoreExcludedNodes(el, excludedNodes);
  }

  if (!split.lines || split.lines.length === 0) {
    gsap.set(el, { opacity: 1, visibility: 'visible' });
    return;
  }

  splitTextInstances.push(split);

  gsap.set(split.lines, { 
    opacity: 0, 
    y: 60, 
    display: 'flex', 
    alignItems: 'center'
  });

  gsap.set(el, { visibility: 'visible', opacity: 1 });

  gsap.to(split.lines, {
    opacity: 1,
    y: 0,
    duration: config.durations.splitText,
    delay: delay,
    ease: config.easing.default,
    stagger: {
      each: config.stagger.splitText * 1.5,
      from: "start",
      ease: "power2.out"
    }
  });

  if (excludedNodes.length > 0) {
    const originalRevert = split.revert.bind(split);
    const nodesSnapshot = excludedNodes.map(item => ({
      node: item.node,
      placeholderId: item.placeholderId
    }));
    split.revert = function() {
      originalRevert();
      restoreExcludedNodes(el, nodesSnapshot);
    };
  }
  
  const isMobile = window.innerWidth <= 768;
  const startPosition = useWrapperTrigger ? 'top top' : (isMobile ? 'top 15%' : 'bottom 50%');
  const endPosition = useWrapperTrigger ? 'bottom top' : 'top 50%';

  //gsap.set(el, { yPercent: 100 });

  const followTween = gsap.to(el, {
    yPercent: 0,
    ease: 'none',
    immediateRender: false,
    scrollTrigger: {
      trigger: triggerElement,
      start: useWrapperTrigger ? 'top 50%' : 'top 50%',
      end: useWrapperTrigger ? 'bottom top' : '+=80%',
      scrub: true,
      onLeave: () => {
        gsap.set(el, { yPercent: 0, clearProps: 'transform' });
      },
      onLeaveBack: () => {
        gsap.set(el, { yPercent: 0, clearProps: 'transform' });
      },
      onRefresh: (self) => {
        if (self.progress === 0 || self.progress === 1) {
          gsap.set(el, { yPercent: 0, clearProps: 'transform' });
        }
      }
    }
  });
  
  if (followTween && followTween.scrollTrigger) {
    titleScrollTriggers.push(followTween.scrollTrigger);
  }
  
  const titleTrigger = ScrollTrigger.create({
    trigger: triggerElement,
    toggleActions: "none none none none",
    start: startPosition,
    end: endPosition,
    scrub: false,
    onLeave: () => {
      gsap.killTweensOf(split.lines);
      
      gsap.to(split.lines, {
        opacity: 0,
        yPercent: 0,
        duration: config.durations.splitText,
        ease: config.easing.default,
        stagger: {
          each: config.stagger.splitText * 1.5,
          direction: -1,
          ease: "power2.out"
        }
      });
    },
    onEnterBack: () => {
      gsap.killTweensOf(split.lines);
      
      gsap.set(split.lines, { 
        opacity: 0, 
        y: 60,
        yPercent: 0
      });
      
      gsap.to(split.lines, {
        opacity: 1,
        y: 0,
        duration: config.durations.splitText,
        ease: config.easing.default,
        stagger: {
          each: config.stagger.splitText * 1.5,
          from: "start",
          ease: "power2.out"
        }
      });
    }
  });
  
  titleScrollTriggers.push(titleTrigger);
}

function animateTitleLogo(el, delay) {
  const config = DEFAULT_CONFIG;
  
  gsap.set(el, { opacity: 0, visibility: 'visible' });

  const triggerElement = el.closest('[data-title-anchor]') || el;
  const useWrapperTrigger = triggerElement !== el;
  
  gsap.set(el, { 
    opacity: 0, 
    y: 60
  });
  
  gsap.set(el, { visibility: 'visible', opacity: 1 });
  
  gsap.to(el, {
    opacity: 1,
    y: 0,
    duration: config.durations.splitText,
    delay: delay,
    ease: config.easing.default
  });
  
  const isMobile = window.innerWidth <= 768;
  const startPosition = useWrapperTrigger ? 'top top' : (isMobile ? 'top 15%' : 'bottom 50%');
  const endPosition = useWrapperTrigger ? 'bottom top' : 'top 50%';

  gsap.set(el, { yPercent: 0 });

  const followTween = gsap.to(el, {
    yPercent: -40,
    ease: 'none',
    immediateRender: false,
    scrollTrigger: {
      trigger: triggerElement,
      start: useWrapperTrigger ? 'top top' : 'top 20%',
      end: useWrapperTrigger ? 'bottom top' : '+=120%',
      scrub: true
    }
  });
  
  if (followTween && followTween.scrollTrigger) {
    titleScrollTriggers.push(followTween.scrollTrigger);
  }
  
  const logoTrigger = ScrollTrigger.create({
    trigger: triggerElement,
    toggleActions: "none none none none",
    start: startPosition,
    end: endPosition,
    scrub: false,
    onLeave: () => {
      gsap.killTweensOf(el);
      
      gsap.to(el, {
        opacity: 0,
        y: -60,
        duration: config.durations.splitText,
        ease: config.easing.default
      });
    },
    onEnterBack: () => {
      gsap.killTweensOf(el);
      
      gsap.set(el, { 
        opacity: 0, 
        y: 60
      });
      
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: config.durations.splitText,
        ease: config.easing.default
      });
    }
  });
  
  titleScrollTriggers.push(logoTrigger);
}

function animateFadeUp(el, delay) {
  if (!el) {
    return;
  }
  
  const config = DEFAULT_CONFIG;
  
  const nestedEffects = el.querySelectorAll(EFFECT_SELECTOR);
  if (nestedEffects.length > 0) {
    gsap.set(nestedEffects, { opacity: 0, visibility: 'hidden' });
  }
  gsap.set(el, {
    opacity: 0,
    visibility: 'hidden',
    y: '60px'
  });
  gsap.to(el, {
    opacity: 1,
    visibility: 'visible',
    y: '0px',
    duration: config.durations.fadeUp,
    delay: delay,
    ease: config.easing.default,
    force3D: true
  });
}

function animateFadeDown(el, delay) {
  if (!el) {
    return;
  }
  
  const nestedEffects = el.querySelectorAll(EFFECT_SELECTOR);
  if (nestedEffects.length > 0) {
    gsap.set(nestedEffects, { opacity: 0, visibility: 'hidden' });
  }

  const config = DEFAULT_CONFIG;
  
  gsap.fromTo(el, {
    opacity: 0,
    y: -200,
    visibility: 'visible'
  }, {
    opacity: 1,
    y: 0,
    duration: config.durations.fadeDown,
    delay: delay,
    ease: config.easing.default,
    force3D: true
  });
}

function animateTypewriter(el, delay) {
  const config = DEFAULT_CONFIG;
  const originalChildren = Array.from(el.childNodes);
  const fullText = el.textContent.trim();
  el.innerHTML = '';
  
  gsap.set(el, { visibility: 'visible', opacity: 1 });

  const cursor = document.createElement('span');
  cursor.classList.add('cursor');
  cursor.textContent = '|';
  el.appendChild(cursor);

  let i = 0;
  function typeChar() {
    if (i < fullText.length) {
      const letter = document.createElement('span');
      letter.textContent = fullText[i];
      el.insertBefore(letter, cursor);

      gsap.from(letter, {
        opacity: 0,
        duration: config.durations.typewriter,
        ease: 'none'
      });

      i++;
      gsap.delayedCall(0.1, typeChar);
    } else {
      cursor.remove();
    }
  }

  gsap.delayedCall(delay + 0.5, typeChar);
}

function animateListItems(el, delay) {
  if (!el) {
    return;
  }
  
  const config = DEFAULT_CONFIG;
  const listItems = el.querySelectorAll('li');
  
  if (listItems.length === 0) {
    gsap.set(el, { opacity: 1, visibility: 'visible' });
    return;
  }
  
  gsap.set(el, { opacity: 0, visibility: 'visible' });
  
  gsap.set(el, { visibility: 'visible', opacity: 1 });
  gsap.set(listItems, { 
    opacity: 0, 
    y: 30
  });
  
  gsap.to(listItems, {
    opacity: 1,
    y: 0,
    duration: config.durations.listItems,
    delay: delay,
    ease: config.easing.default,
    stagger: config.stagger.listItems
  });
}